import { ChangeEventHandler, useContext, useEffect, useState } from 'react'
import { ApplicationContext } from '../../../../../data'
import moment, { Moment } from 'moment'
import { appEventEmitter, processLoading } from '../../../../../initialization'
import { asyncGetTaskSchedule } from '../../../../../services/booking.service'
import { FilterDataType, ScheduleSearchFilterComponent } from '../schedule-filter.component'
import {
  AccountInfoDto,
  CategoryLevel,
  ServiceCategoryDto,
  TaskDto,
  TaskFilterQuery,
  VendorInfoDto
} from '../../../../../definition'
import { ScheduleCalendarConfigurationType } from '../../../../../definition/time-slot.dto'
import { toast } from 'react-toastify'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Checkbox, CheckboxProps, Col, DatePicker, DatePickerProps, Input, Popover, Row, Select } from 'antd'
import { asyncGetListTechnicians } from '../../../../../services/technician.service'
import { debounce } from 'lodash'
import searchIcon from './../../../../../styles/images/search-icon.png'
import NewBookings from './new-booking-list'
import ScheduleCalendarWeekly from './schedule-calendar-weekly'
import ScheduleCalendarDaily from './schedule-calender-daily'
import { deleteUrlParam, updateUrlParam } from '../../../../../utils'
import { ModalNode } from '../../../../components/modal.component'
import { APP_EVENTS } from '../../../../../constants'
import { ConfigOperatingDay } from '../config-operating-day'
import { asyncGetListVendors } from '../../../../../services/vendors.service'
import { NavLink } from 'react-router-dom'
import { asyncGetServiceCategories } from '../../../../../services'
import { BlockOffEmployeeTimeModal } from '../block-off-employee-time'
import Refresh from './../../../../../styles/images/refresh.svg'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'

type ScheduleStateType = {
  selectionTechnicianSearching?: string
  isLoadingTechnician?: boolean
  selectedVendorId?: string[]
  selectedMainCategoryId?: string[]

  selectedDate: Moment
  startDate: Moment
  endDate: Moment

  filterData: FilterDataType
  tasks?: TaskDto[]

  configuration?: ScheduleCalendarConfigurationType
}

const generateWeekDates = (selectedDate: Moment) => {
  const startDate = moment(selectedDate).startOf('week')
  const endDate = moment(selectedDate).endOf('week')

  return { startDate, endDate, selectedDate }
}

const dateFormat = 'DD MMM YYYY'

const TimeTable = () => {
  const { contextData } = useContext(ApplicationContext)
  const configuration = contextData.authenticationData?.configuration
  const [technicians, setTechnicians] = useState<AccountInfoDto[]>([])
  const [vendors, setVendors] = useState<VendorInfoDto[]>([])
  const [mainCategories, setMainCategories] = useState<ServiceCategoryDto[]>([])
  const [subCategories, setSubCategories] = useState<ServiceCategoryDto[]>([])
  const [childCategories, setChildCategories] = useState<ServiceCategoryDto[]>([])
  const [mode, setMode] = useState<'weekly' | 'daily'>('weekly')
  const [isAllMain, setIsAllMain] = useState(true)
  const [isAllVendor, setIsAllVendor] = useState(true)

  const [state, setState] = useState<ScheduleStateType>({
    ...generateWeekDates(moment()),
    filterData: {},
    configuration
  })

  const onLoadTasks = (
    weekTime: { startDate?: Moment; endDate?: Moment; selectedDate: Moment },
    filterData?: FilterDataType,
    selectedVendorId?: string[],
    configuration?: ScheduleCalendarConfigurationType,
    selectedMainCategoryId?: string[]
  ) => {
    const params: TaskFilterQuery = {
      startTime: weekTime.startDate?.toDate() as Date,
      endTime: weekTime.endDate?.toDate() as Date,
      vendorIds: isAllVendor ? undefined : selectedVendorId?.join(','),
      mainCategoryIds: isAllMain ? undefined : selectedMainCategoryId?.join(','),
      reschedule: false
    }

    filterData?.assigned && (params.assigned = true)
    filterData?.reAssigned && (params.reAssigned = true)
    filterData?.contractBookings && (params.contractBooking = true)
    filterData?.oneTimeBookings && (params.oneTimeBooking = true)
    filterData?.reschedule && (params.reschedule = true)
    filterData?.recurringBookingRequest && (params.recurringBookingRequest = true)
    filterData?.newBooking && (params.newBooking = true)
    filterData?.notes && (params.notes = true)
    filterData?.statuses && (params.statuses = filterData.statuses)
    filterData?.search && (params.search = filterData.search)

    processLoading(
      asyncGetTaskSchedule(params)
        .then((res) => {
          setState({
            ...state,
            startDate: weekTime.startDate || state.startDate,
            endDate: weekTime.endDate || state.endDate,
            selectedDate: weekTime.selectedDate || state.selectedDate,
            filterData: filterData || state.filterData,
            selectedVendorId,
            selectedMainCategoryId,
            tasks: res.items,
            configuration,
            selectionTechnicianSearching: ''
          })
        })
        .catch((error) => {
          setState({
            ...state,
            startDate: weekTime.startDate || state.startDate,
            endDate: weekTime.endDate || state.endDate,
            selectedDate: weekTime.selectedDate || state.selectedDate,
            filterData: filterData || state.filterData,
            selectedVendorId,
            selectedMainCategoryId,
            selectionTechnicianSearching: '',
            configuration
          })
          toast.error(`Can not get tasks!${error.response?.data?.error?.message || error.message}`)
        })
    )
  }

  useEffect(() => {
    fetchStasticData().then(({ mainCategorieIds, listVendorIds }) => {
      onLoadTasks(
        {
          startDate: state.startDate,
          endDate: state.endDate,
          selectedDate: state.selectedDate
        },
        state.filterData,
        listVendorIds,
        configuration,
        mainCategorieIds
      )
    })
  }, [])

  const fetchStasticData = async () => {
    asyncGetListTechnicians({ all: true }).then((res) => {
      if (res) {
        setTechnicians(res?.items || [])
      }
    })

    asyncGetServiceCategories({ all: true, level: CategoryLevel.Sub }).then((res) => {
      if (res) {
        setSubCategories(res?.items || [])
      }
    })
    asyncGetServiceCategories({ all: true, level: CategoryLevel.Child }).then((res) => {
      if (res) {
        setChildCategories(res?.items || [])
      }
    })

    const mainCategoriesRes = await asyncGetServiceCategories({
      level: CategoryLevel.Main,
      all: true,
      includeAll: true
    })
    const mainCategories = (mainCategoriesRes?.items || []).filter((cat) => cat.name !== 'All')
    setMainCategories(mainCategories)

    const listVendorRes = await asyncGetListVendors({ all: true })
    const listVendor = listVendorRes?.items || []
    setVendors(listVendor)

    return {
      mainCategorieIds: mainCategories.map((cat) => cat.id),
      listVendorIds: listVendor.map((v) => v.id)
    }
  }

  const refreshData = () => {
    fetchStasticData()
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      state.filterData,
      state.selectedVendorId,
      state.configuration,
      state.selectedMainCategoryId
    )
  }

  const onWeekChange = (selectedDate: Moment | null) => {
    onLoadTasks(
      { ...generateWeekDates(selectedDate || moment()) },
      state.filterData,
      state.selectedVendorId,
      state.configuration,
      state.selectedMainCategoryId
    )
  }

  const onDailyChange = (selectedDate: Moment | null) => {
    onLoadTasks(
      {
        startDate: moment(selectedDate).startOf('day'),
        endDate: moment(selectedDate).endOf('day'),
        selectedDate: state.selectedDate
      },
      state.filterData,
      state.selectedVendorId,
      configuration,
      state.selectedMainCategoryId
    )
  }

  const onSelectVendor = (vendorIds?: string[]) => {
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      state.filterData,
      vendorIds,
      state.configuration,
      state.selectedMainCategoryId
    )
  }

  const onFilterChange = (filterData: FilterDataType) => {
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      filterData,
      state.selectedVendorId,
      state.configuration,
      state.selectedMainCategoryId
    )
  }

  const onSearching: ChangeEventHandler<HTMLInputElement> = (e) => {
    asyncGetListTechnicians({ all: true, search: e.target.value, searchFor: 'calendar' }).then((res) => {
      if (res) {
        setTechnicians(res?.items || [])
      }
    })
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      { ...state.filterData, search: e.target.value },
      state.selectedVendorId,
      state.configuration,
      state.selectedMainCategoryId
    )
  }

  const onChangeMode = (mode: 'weekly' | 'daily') => {
    if (mode === 'weekly') {
      setMode('weekly')
      onLoadTasks(
        { ...generateWeekDates(moment()) },
        state.filterData,
        state.selectedVendorId,
        configuration,
        state.selectedMainCategoryId
      )
    } else {
      setMode('daily')
      onLoadTasks(
        {
          startDate: moment().startOf('day'),
          endDate: moment().endOf('day'),
          selectedDate: state.selectedDate
        },
        state.filterData,
        state.selectedVendorId,
        configuration,
        state.selectedMainCategoryId
      )
    }
  }

  const onConfigureOperatingHouse = () => {
    updateUrlParam({
      add: { configureOperatingHouse: 'true' }
    })

    appEventEmitter.emit<ModalNode>(APP_EVENTS.app_modal, {
      status: true,
      node: ConfigOperatingDay,
      title: 'Configure Operating  Days',
      onClose: () => {
        deleteUrlParam('configureOperatingHouse')
      },
      onDone: () => {
        deleteUrlParam('configureOperatingHouse')
        refreshData()
      }
    })
  }

  const onConfigureBlockTime = () => {
    updateUrlParam({
      add: { blockOffEmployeeTime: 'true' }
    })

    appEventEmitter.emit<ModalNode>(APP_EVENTS.app_modal, {
      status: true,
      node: BlockOffEmployeeTimeModal,
      title: 'Block Off Employee’s Time',
      onClose: () => {
        deleteUrlParam('blockOffEmployeeTime')
      },
      onDone: () => {
        deleteUrlParam('blockOffEmployeeTime')
        refreshData()
      }
    })
  }

  const onSetTimeToday = () => {
    if (mode === 'weekly') {
      onLoadTasks(
        { ...generateWeekDates(moment()) },
        state.filterData,
        state.selectedVendorId,
        configuration,
        state.selectedMainCategoryId
      )
    } else {
      onLoadTasks(
        {
          startDate: moment().startOf('day'),
          endDate: moment().endOf('day'),
          selectedDate: state.selectedDate
        },
        state.filterData,
        state.selectedVendorId,
        configuration,
        state.selectedMainCategoryId
      )
    }
  }

  const filterOptionWithLabalIsElement = (input: string, option?: { name?: string; value?: string }) => {
    return (option?.name ?? '').toLowerCase().includes(input.toLowerCase())
  }

  const customWeekStartEndFormat: DatePickerProps['format'] = (value) =>
    `${moment(value).startOf('week').format(dateFormat)} ~ ${moment(value).endOf('week').format(dateFormat)}`

  const checkAllMain = mainCategories.length === state.selectedMainCategoryId?.length
  const indeterminateMain =
    (state.selectedMainCategoryId || [])?.length > 0 &&
    (state.selectedMainCategoryId || [])?.length < mainCategories.length

  const onCheckAllMainCategoryChange: CheckboxProps['onChange'] = (e) => {
    if (e.target.checked) {
      setIsAllMain(true)
      onLoadTasks(
        {
          startDate: state.startDate,
          endDate: state.endDate,
          selectedDate: state.selectedDate
        },
        state.filterData,
        state.selectedVendorId,
        state.configuration,
        mainCategories.map((cat) => cat.id)
      )
    } else {
      setIsAllMain(false)
      onLoadTasks(
        {
          startDate: state.startDate,
          endDate: state.endDate,
          selectedDate: state.selectedDate
        },
        state.filterData,
        state.selectedVendorId,
        state.configuration,
        []
      )
    }
  }

  const onChangeMainCategory = (list: CheckboxValueType[]) => {
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      state.filterData,
      state.selectedVendorId,
      state.configuration,
      list as string[]
    )
  }

  const checkAllVendor = vendors.length === state.selectedVendorId?.length
  const indeterminateVendor =
    (state.selectedVendorId || [])?.length > 0 && (state.selectedVendorId || [])?.length < vendors.length

  const onCheckAllVendorChange: CheckboxProps['onChange'] = (e) => {
    if (e.target.checked) {
      setIsAllVendor(true)
      onLoadTasks(
        {
          startDate: state.startDate,
          endDate: state.endDate,
          selectedDate: state.selectedDate
        },
        state.filterData,
        vendors.map((vendor) => vendor.id),
        state.configuration,
        state.selectedMainCategoryId
      )
    } else {
      setIsAllVendor(false)
      onLoadTasks(
        {
          startDate: state.startDate,
          endDate: state.endDate,
          selectedDate: state.selectedDate
        },
        state.filterData,
        [],
        state.configuration,
        state.selectedMainCategoryId
      )
    }
  }

  const onChangeVendor = (list: CheckboxValueType[]) => {
    onLoadTasks(
      {
        startDate: state.startDate,
        endDate: state.endDate,
        selectedDate: state.selectedDate
      },
      state.filterData,
      list as string[],
      state.configuration,
      state.selectedVendorId
    )
  }

  const debounceOnSeaching = debounce(onSearching, 1000)

  return (
    <div className='calender-layout'>
      <div className='calender-grid'>
        <div className='span-12 schedule-legend-config-2'>
          <div className='schedule-legend'>
            <span>Legend:</span>
            <div className='legend new-booking'>New Booking</div>
            {/* <span className='legend re-assigned'>Reassign Required</span> */}
            <div className='legend reschedule'>Reschedule Required</div>
            <div className='legend assigned'>Assigned</div>
            <div className='legend on-the-way'>On The Way</div>
            <div className='legend task-started'>Task Started</div>
            <div className='legend finalising'>Finalising</div>
            <div className='legend completed'>Completed</div>
            <div className='legend canceled'>Canceled</div>
            <div className='legend reserved'>Reserved</div>
          </div>
        </div>
        <div className='span-12 schedule-select-date-assignee'>
          <Button onClick={refreshData} className='refresh-btn'>
            <img src={Refresh} alt={'refresh'} width={10} />
          </Button>
          <Button onClick={onSetTimeToday}>Today</Button>
          <Select
            style={{ width: 100 }}
            options={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'daily', label: 'Daily' }
            ]}
            value={mode}
            onChange={onChangeMode}
          />
          {mode === 'weekly' && (
            <div className='schedule-select-date'>
              <DatePicker
                inputReadOnly
                suffixIcon={undefined}
                clearIcon={undefined}
                allowClear={false}
                onChange={onWeekChange}
                className='date-picker'
                picker='week'
                value={state.selectedDate}
                format={customWeekStartEndFormat}
              />
              <LeftOutlined
                color='black'
                onClick={() => {
                  const selectedDate = moment(state.selectedDate)
                  selectedDate.date(selectedDate.date() - 7)
                  onWeekChange(selectedDate)
                }}
              />
              <RightOutlined
                color='black'
                onClick={() => {
                  const selectedDate = moment(state.selectedDate)
                  selectedDate.date(selectedDate.date() + 7)
                  onWeekChange(selectedDate)
                }}
              />
            </div>
          )}
          {mode === 'daily' && (
            <DatePicker value={state.startDate} format={dateFormat} onChange={onDailyChange} allowClear={false} />
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Button className='link-btn' onClick={onConfigureOperatingHouse}>
              Configure Operating Hours
            </Button>
            <Button className='outline-btn' onClick={onConfigureBlockTime}>
              Block Off Employee’s Time
            </Button>
          </div>
        </div>
        <div className='span-12 schedule-filter'>
          <Popover
            placement='bottom'
            title={<div>Status</div>}
            content={<ScheduleSearchFilterComponent filterData={state.filterData} onChange={onFilterChange} />}
          >
            <Button>
              Status <LeftOutlined color='black' style={{ rotate: '270deg' }} />
            </Button>
          </Popover>
          <Popover
            placement='bottom'
            title={<div>Filter Main Category</div>}
            content={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Checkbox
                  indeterminate={indeterminateMain}
                  onChange={onCheckAllMainCategoryChange}
                  checked={checkAllMain}
                >
                  Select all
                </Checkbox>
                <Checkbox.Group
                  style={{ width: 360, height: 400, overflowY: 'scroll', overflowX: 'hidden', marginLeft: 16 }}
                  onChange={onChangeMainCategory}
                  value={state.selectedMainCategoryId}
                >
                  <Row>
                    {mainCategories.map((category) => {
                      return (
                        <Col span={24}>
                          <Checkbox value={category.id}>{category.name}</Checkbox>
                        </Col>
                      )
                    })}
                  </Row>
                </Checkbox.Group>
              </div>
            }
          >
            <Button>
              Filter Main Category <LeftOutlined color='black' style={{ rotate: '270deg' }} />
            </Button>
          </Popover>
          <Popover
            placement='bottom'
            title={<div>Filter Vendor</div>}
            content={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Checkbox
                  indeterminate={indeterminateVendor}
                  onChange={onCheckAllVendorChange}
                  checked={checkAllVendor}
                >
                  Select all
                </Checkbox>
                <Checkbox.Group
                  style={{ width: 360, height: 400, overflowY: 'scroll', overflowX: 'hidden', marginLeft: 16 }}
                  onChange={onChangeVendor}
                  value={state.selectedVendorId}
                >
                  <Row>
                    {vendors.map((vendor) => {
                      return (
                        <Col span={24}>
                          <Checkbox value={vendor.id}>{vendor.name}</Checkbox>
                        </Col>
                      )
                    })}
                  </Row>
                </Checkbox.Group>
              </div>
            }
          >
            <Button>
              Filter Vendor <LeftOutlined color='black' style={{ rotate: '270deg' }} />
            </Button>
          </Popover>

          <div className='schedule-search'>
            <Input
              style={{ fontSize: 14 }}
              className='search-input text-gray'
              placeholder='Search'
              onChange={(event) => debounceOnSeaching(event)}
            />
            <img alt='search' className='search-icon' src={searchIcon} />
          </div>
          <NavLink to='/schedule/booking-type' style={{ marginLeft: 'auto' }}>
            <Button type='primary'>Create New Booking</Button>
          </NavLink>
        </div>
        <div className='span-12'>
          {state.tasks && (
            <>
              {mode === 'weekly' && (
                <ScheduleCalendarWeekly
                  startDate={state.startDate}
                  tasks={state.tasks}
                  configuration={state.configuration}
                  vendors={vendors}
                  technicians={technicians}
                  weekStart={moment(state.startDate).startOf('week').toISOString()}
                  mainCategories={mainCategories}
                  selectedMainCategoryId={state.selectedMainCategoryId || []}
                  selectedVendorId={state.selectedVendorId || []}
                  refreshSchedule={refreshData}
                />
              )}
              {mode === 'daily' && (
                <ScheduleCalendarDaily
                  tasks={state.tasks}
                  configuration={state.configuration}
                  date={state.startDate}
                  vendors={vendors}
                  technicians={technicians}
                  mainCategories={mainCategories}
                  selectedMainCategoryId={state.selectedMainCategoryId || []}
                  selectedVendorId={state.selectedVendorId || []}
                  refreshSchedule={refreshData}
                />
              )}
            </>
          )}
        </div>
      </div>
      <NewBookings mainCategories={mainCategories} subCategories={subCategories} childCategories={childCategories} />
    </div>
  )
}

export default TimeTable
